import Presenter from '../../../abstracts/Presenter'
import { SearchVM } from './ViewModel'
import { SearchUnknownDestinationPresenter } from '../usecases/SearchUnknownDestination/SearchUnknownDestinationPresenter'

import type {
  HitResultType,
  SearchHousesAndDestinationsPresentation,
} from '../usecases/SearchHousesAndDestinations/interfaces/SearchHousesAndDestinationsPresentation'
import type { GetHousePeriodsPresentation } from '../usecases/GetHousePeriods/interfaces/GetHousePeriodsPresentation'
import type { GetTopDestinationsPresentation } from '../usecases/GetTopDestinations/interfaces/GetTopDestinationsPresentation'
import type { SearchHousesPresentation } from '../usecases/SearchHouses/interfaces/SearchHousesPresentation'
import type { GetHouseDetailsPresentation } from '../usecases/GetHouseDetails/interfaces/GetHouseDetailsPresentation'
import type { OrderRetrievedFacetsPresentation } from '../usecases/OrderRetrievedFacets/interfaces/OrderRetrievedFacetsPresentation'
import type { HousePeriodsResponseDataFormatted } from '../usecases/GetHousePeriods/types'
import type { MakeHouseRequestPresentation } from '../usecases/MakeHouseRequest/interfaces/MakeHouseRequestPresentation'
import type { GetHousePeriodsAndBookingsPresentation } from '../usecases/GetHousePeriodsAndBookings/interfaces/GetHousePeriodsAndBookingsPresentation'
import type { GetHouseTruthLinksPresentation } from '../usecases/GetHouseTruthLinks/interfaces/GetHouseTruthLinksPresentation'
import type { UpdateInquiryPresentation } from '../usecases/UpdateInquiry/interfaces/UpdateInquiryPresentation'
import type { GetNbHiddenHousesPresentation } from '../usecases/GetNbHiddenHouses/interfaces/GetNbHiddenHousesPresentation'
import type { SearchHousesMapPresentation } from '../usecases/SearchHousesMap/interfaces/SearchHousesMapPresentation'
import type { GetHouseKeyFeaturesPresentation } from '../usecases/GetHouseKeyFeatures/interfaces/GetHouseKeyFeaturesPresentation'
import type { GetHouseIncludedServicesPresentation } from '../usecases/GetHouseIncludedServices/interfaces/GetHouseIncludedServicesPresentation'
import type { GetAreaEquipmentsPresentation } from '../usecases/GetAreaEquipments/interfaces/GetAreaEquipmentsPresentation'
import type { GetDestinationFavoritePeriodPresentation } from '../usecases/GetDestinationFavoritePeriod/interfaces/GetDestinationFavoritePeriodPresentation'
import type { GetSimilarHousesPricesByPeriodPresentation } from '../usecases/GetSimilarHousesPricesByPeriod/interfaces/GetSimilarHousesPricesByPeriodPresentation'
import type { SearchDestinationsPresentation } from '../usecases/SearchDestinations/interfaces/SearchDestinationsPresentation'
import type { SearchHousesSplitMapPresentation } from '../usecases/SearchHousesSplitMap/interfaces/SearchHousesSplitMapPresentation'
import type { GetHouseNextValidPeriodPresentation } from '../usecases/GetHouseNextValidPeriod/interfaces/GetHouseNextValidPeriodPresentation'

import type {
  AppsSource,
  BookingsPeriods,
  CoordinatesBoundaries,
  FacetsRetrieve,
  HouseDetailsAttributes,
  HouseIncludedService,
  HousePhotosAttributesFormatted,
  HousesCoordinates,
  HouseTruthLink,
  PeriodDurationInquiry,
  SearchDestinations,
  SearchHouse,
  SimilarHousePrices,
  SearchHouseCoordinatesFormatted,
} from '../../../types'
import type { UpdateInquiryResponseData } from '../usecases/UpdateInquiry/types'
import type { MakeHouseRequestResponseData } from '../usecases/MakeHouseRequest/types'
import type {
  HouseAreaEquipment,
  HouseKeyFeatures,
} from '../usecases/GetHouseDetails/types'
import type { ApiError } from 'lc-repository/dist/repositories/types'
import type { HousePeriodsIdResponseData } from 'lc-repository/dist/repositories/lc/v3/houses/periods'
import type { UnknownDestinationModel } from '../usecases/SearchUnknownDestination/interfaces/GeoCodingRepository'

export default class SearchPresenter<AppSource extends AppsSource = 'web'>
  extends Presenter<SearchVM>
  implements
    GetHouseDetailsPresentation,
    GetTopDestinationsPresentation,
    OrderRetrievedFacetsPresentation,
    SearchHousesAndDestinationsPresentation,
    SearchHousesSplitMapPresentation,
    SearchHousesPresentation,
    GetHousePeriodsPresentation,
    MakeHouseRequestPresentation,
    GetHousePeriodsAndBookingsPresentation,
    UpdateInquiryPresentation,
    GetNbHiddenHousesPresentation,
    SearchHousesMapPresentation,
    GetHouseKeyFeaturesPresentation,
    GetAreaEquipmentsPresentation,
    GetHouseIncludedServicesPresentation,
    GetHouseTruthLinksPresentation,
    GetDestinationFavoritePeriodPresentation,
    GetSimilarHousesPricesByPeriodPresentation,
    SearchDestinationsPresentation,
    GetHouseNextValidPeriodPresentation
{
  constructor(protected source: AppsSource = 'web') {
    super(new SearchVM())
  }

  displayError(error: ApiError): void {
    this.vm.error = error
    this.notifyVM()
  }

  displayHouseAreaEquipments(houseAreaEquipments: HouseAreaEquipment[]): void {
    this.vm.houseAreaEquipments = houseAreaEquipments
    this.notifyVM()
  }

  displayHouseKeyFeatures(houseKeyFeatures: HouseKeyFeatures): void {
    this.vm.houseKeyFeatures = houseKeyFeatures
    this.notifyVM()
  }

  displayNbHiddenHouses(nb: number): void {
    this.vm.nbHiddenHouses = nb
    this.notifyVM()
  }

  displayHousesPeriodIds(housesPeriodIds: {
    validHousePeriodsIds: number[]
    invalidHousePeriodsIds: number[]
  }): void {
    this.vm.housesPeriodIds = housesPeriodIds
    this.notifyVM()
  }

  displayUpdateInquiry(updatedInquiry: UpdateInquiryResponseData): void {
    this.vm.updatedInquiry = updatedInquiry
    this.notifyVM()
  }

  displayUpdateInquiryError(error: string): void {
    this.vm.inquiryError = error
    this.notifyVM()
  }

  displayPeriodDurations(periodsDurations?: PeriodDurationInquiry[]): void {
    this.vm.periodsDurations = periodsDurations
    this.notifyVM()
  }

  displayBookings(bookings?: BookingsPeriods): void {
    this.vm.bookings = bookings
    this.notifyVM()
  }

  displayHousePhotos(housePhotos: HousePhotosAttributesFormatted | null): void {
    this.vm.housePhotos = housePhotos
    this.notifyVM()
  }

  displayHouseDetails(
    houseDetails: HouseDetailsAttributes & { houseUrl: string },
  ): void {
    this.vm.houseDetails = houseDetails
    this.notifyVM()
  }

  displayHousePeriods(housePeriods: HousePeriodsResponseDataFormatted): void {
    this.vm.housePeriods = housePeriods
    this.notifyVM()
  }

  displaySearchCurrentPage(nb: number): void {
    this.vm.searchCurrentPage = nb
    this.notifyVM()
  }

  displaySearchHousesSplitListResult(result: SearchHouse[]): void {
    this.vm.searchHousesSpitListResult = result
    this.notifyVM()
  }

  displaySearchHousesSplitMapResult(
    result: SearchHouseCoordinatesFormatted[],
  ): void {
    this.vm.searchHousesSpitMapResult = result
    this.notifyVM()
  }

  displayFacets(facets: FacetsRetrieve): void {
    this.vm.facets = facets
    this.notifyVM()
  }

  displayOrderedFacets(orderedFacets: FacetsRetrieve): void {
    this.vm.orderedFacets = orderedFacets
    this.notifyVM()
  }

  displaySearchHousesResult(result: SearchHouse[]): void {
    this.vm.searchHousesResult = result
    this.notifyVM()
  }

  displayHousesStorage(houses: SearchHouse[]): void {
    this.vm.housesStorage = houses
    this.notifyVM()
  }

  displayAlgoliaQueryId(queryId?: string): void {
    this.vm.algoliaQueryId = queryId
    this.notifyVM()
  }

  displayNbHouses(nb: number): void {
    this.vm.nbHouses = nb
    this.notifyVM()
  }

  displayHousesCoordinates(coordinates: HousesCoordinates | undefined): void {
    this.vm.houseCoordinates = coordinates
    this.notifyVM()
  }

  displayCoordinatesBoundaries(
    coordinatesBoundaries: CoordinatesBoundaries | undefined,
  ): void {
    this.vm.coordinatesBoundaries = coordinatesBoundaries
    this.notifyVM()
  }

  displayNbPages(nb: number): void {
    this.vm.nbPages = nb
    this.notifyVM()
  }

  displayTopDestinationsResult(results: HitResultType): void {
    this.vm.topDestinationsResult = results
    this.notifyVM()
  }

  displayHousesResult(results: HitResultType): void {
    this.vm.housesResult = results
    this.notifyVM()
  }

  displayDestinationsResult(results: HitResultType): void {
    this.vm.destinationsResult = results
    this.notifyVM()
  }

  displayMessage(msg: string) {
    this.vm.msg = msg
    this.notifyVM()
  }

  displayMakeHouseRequest(houseRequest: MakeHouseRequestResponseData): void {
    this.vm.houseRequest = houseRequest
    this.notifyVM()
  }

  displayHouseRequestError(error: string): void {
    this.vm.houseRequestError = error
    this.notifyVM()
  }

  displayHousePeriodsById(
    housePeriodsById: HousePeriodsIdResponseData | null,
  ): void {
    this.vm.housePeriodsById = housePeriodsById
    this.notifyVM()
  }

  displayHouseIncludedServices(
    houseIncludedServices: HouseIncludedService[],
  ): void {
    this.vm.houseIncludedServices = houseIncludedServices
    this.notifyVM()
  }

  displayHouseTruthLinks(
    houseTruthLinks: Pick<HouseTruthLink, 'description'>[],
  ): void {
    this.vm.houseTruthLinks = houseTruthLinks
    this.notifyVM()
  }

  displayDestinationFavoritePeriod(favoritePeriod: {
    percent: number
    period: string
  }) {
    this.vm.destinationFavoritePeriod = favoritePeriod
  }

  displayDestinationPhoto(destinationPhoto: { id: string; photo?: string }) {
    this.vm.destinationPhoto = destinationPhoto
    this.notifyVM()
  }

  displayHousesPrices(houseResult: SimilarHousePrices[] | null) {
    this.vm.similarHousePrices = houseResult
    this.notifyVM()
  }

  displayDestinations(destinations: SearchDestinations[]) {
    this.vm.destinations = destinations
  }

  displayHouseNextValidPeriod(
    range: { startDate: string; endDate: string; houseId: string } | null,
  ): void {
    this.vm.houseNextPeriod = range
  }

  displayUnknownDestinations(model: UnknownDestinationModel, query: string) {
    return new SearchUnknownDestinationPresenter<AppSource>(
      this.source,
    ).display(model, query)
  }
}
